import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { createRoom } from "../lib/room";
import { quickMatch } from "../lib/publicRooms";
import GuestForm from "./GuestForm";
import { Modal } from "./ui";
import { currentLang, useT } from "../i18n";

// "Create a room" for any game. Visitors who aren't signed in pick a nickname
// first; the room is created as soon as they have.
export function useCreateRoom() {
  const { user } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [pendingGame, setPendingGame] = useState(null);

  const create = useCallback(
    async (game) => {
      if (!user) {
        setPendingGame(game);
        return;
      }
      setBusy(game);
      try {
        // The room's words and prompts start in the host's language.
        const code = await createRoom(game, user.uid, currentLang());
        navigate(`/r/${code}`);
      } catch (e) {
        toast({ emoji: "warning", kind: "error", title: t("Couldn't create room"), body: e.message });
      } finally {
        setBusy(null);
      }
    },
    [user, navigate, toast, t]
  );

  useEffect(() => {
    if (user && pendingGame) {
      setPendingGame(null);
      create(pendingGame);
    }
  }, [user, pendingGame, create]);

  const modal = (
    <Modal open={!!pendingGame && !user} onClose={() => setPendingGame(null)} title={t("Pick a nickname")}>
      <h2 className="modal-title">{t("Pick a nickname")}</h2>
      <GuestForm cta={t("Create room")} />
    </Modal>
  );
  return { create, busy, modal };
}

// "Quick match": joins a public lobby of strangers, or starts one when there's
// none. Visitors who aren't signed in pick a nickname first, like for a new room.
export function useQuickMatch() {
  const { user } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);

  const go = useCallback(async () => {
    if (!user) {
      setPending(true);
      return;
    }
    setBusy(true);
    try {
      const { code, created } = await quickMatch(user.uid, currentLang());
      if (created) toast({ emoji: "bolt", title: t("No open lobby right now, so you started one"), body: t("Other Quick match players will join you.") });
      navigate(`/r/${code}`);
    } catch (e) {
      toast({ emoji: "warning", kind: "error", title: t("Couldn't find a match"), body: e.message });
    } finally {
      setBusy(false);
    }
  }, [user, navigate, toast, t]);

  useEffect(() => {
    if (user && pending) {
      setPending(false);
      go();
    }
  }, [user, pending, go]);

  const modal = (
    <Modal open={pending && !user} onClose={() => setPending(false)} title={t("Pick a nickname")}>
      <h2 className="modal-title">{t("Pick a nickname")}</h2>
      <GuestForm cta={t("Quick match")} />
    </Modal>
  );
  return { go, busy, modal };
}
